import type { JSONContent } from '@tiptap/react';
import { getExcerpts, type ExcerptView } from './excerpts';
import { BUILT_IN_FLAG_TYPES } from './flags';
import { evaluateTable, formatResult } from './tableMath';

/** What an excerptEmbed exports as (#277): the excerpt's text and where it came from. */
export interface ExcerptExport { text: string; sourceTitle: string }

/**
 * excerptId -> its export, or null when the embed must render nothing (the
 * excerpt is deleted, or no longer sent to this document). Ids missing from
 * the map render nothing too.
 */
export type ExcerptResolver = ReadonlyMap<string, ExcerptExport | null>;

// The resolver of the conversion in progress. tiptapToMarkdown is synchronous,
// so one conversion owns it from start to finish (set and restored in a
// try/finally), which saves threading it through every node helper.
let activeResolver: ExcerptResolver | undefined;

/**
 * Flags (#316) in an export: a flagged line is prefixed "[Question] ", and an
 * addressed flag's reply follows it as "> Answer: ...". Labels default to the
 * built-in names ("[Flag]" for anything else); replies need `reply`, since the
 * document alone does not hold a flag's state.
 */
export interface FlagExport {
  label?: (type: string) => string;
  reply?: (id: string) => { label: string; reply: string } | null;
}
let activeFlags: FlagExport | undefined;
const builtInLabel = (type: string) => BUILT_IN_FLAG_TYPES.find((t) => t.id === type)?.label ?? 'Flag';

function flagEntries(node: JSONContent): Array<{ id: string; type: string }> {
  const f = node.attrs?.flags;
  return Array.isArray(f) ? f.filter((x) => x && typeof x.id === 'string' && typeof x.type === 'string') : [];
}
function flagPrefix(node: JSONContent): string {
  const label = activeFlags?.label ?? builtInLabel;
  return flagEntries(node).map((f) => `[${label(f.type)}] `).join('');
}
function flagReplies(node: JSONContent): string {
  const reply = activeFlags?.reply;
  if (!reply) return '';
  return flagEntries(node).map((f) => reply(f.id)).filter((r): r is { label: string; reply: string } => !!r)
    .map((r) => `> ${r.label}: ${r.reply.replace(/\s*\n\s*/g, ' ')}\n`).join('');
}

/**
 * Convert TipTap/ProseMirror JSON content to Markdown. Pass `excerpts` to
 * render excerptEmbed nodes (see resolveExcerptEmbeds); without it they render
 * nothing, since the document alone does not hold an embed's text.
 */
export function tiptapToMarkdown(content: JSONContent, excerpts?: ExcerptResolver, flags?: FlagExport): string {
  if (!content || !content.content) return '';
  const previous = activeResolver;
  const previousFlags = activeFlags;
  activeResolver = excerpts;
  activeFlags = flags;
  try {
    const nodes = content.content;
    return nodes
      .map(node => nodeToMarkdown(node))
      // An embed that renders nothing leaves no blank line behind.
      .filter((md, i) => md !== '' || nodes[i].type !== 'excerptEmbed')
      .join('\n');
  } finally {
    activeResolver = previous;
    activeFlags = previousFlags;
  }
}

/** Every excerptEmbed id in a document, in order, without duplicates. */
export function collectExcerptIds(content: JSONContent | null | undefined): string[] {
  const ids: string[] = [];
  const walk = (node: JSONContent | null | undefined) => {
    if (!node || typeof node !== 'object') return;
    const id = node.type === 'excerptEmbed' ? node.attrs?.excerptId : undefined;
    if (typeof id === 'string' && id && !ids.includes(id)) ids.push(id);
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(content);
  return ids;
}

const EXCERPT_BATCH = 200; // GET /api/excerpts?ids= takes at most 200 ids

/**
 * Looks up the embeds of a document for export. An embed resolves only while
 * its excerpt is live AND this document is still one of its destinations -
 * the same rule the editor uses to show it.
 */
export async function resolveExcerptEmbeds(
  content: JSONContent | null | undefined,
  destination: { type: string; id: string },
  fetchExcerpts: (ids: string[]) => Promise<ExcerptView[]> = getExcerpts,
): Promise<ExcerptResolver> {
  const ids = collectExcerptIds(content);
  const out = new Map<string, ExcerptExport | null>();
  for (let i = 0; i < ids.length; i += EXCERPT_BATCH) {
    const views = await fetchExcerpts(ids.slice(i, i + EXCERPT_BATCH));
    for (const v of views) {
      const sentHere = v.destinations.some(d => d.type === destination.type && d.id === destination.id);
      out.set(v.id, v.live && sentHere ? { text: v.plain_text ?? '', sourceTitle: v.source.title } : null);
    }
  }
  return out;
}

function excerptEmbedToMarkdown(node: JSONContent): string {
  const id = node.attrs?.excerptId;
  const found = typeof id === 'string' ? activeResolver?.get(id) : undefined;
  if (!found) return '';
  const lines = found.text.split('\n').map(line => (line.trim() ? `> ${line}` : '>'));
  lines.push(`> -- from ${found.sourceTitle}`);
  return lines.join('\n') + '\n';
}

function namedListItemToMarkdown(item: JSONContent, depth: number, kind: 'checklist' | 'plain', top: boolean): string {
  const [para, ...children] = item.content ?? [];
  const box = (top ? kind === 'checklist' : !!item.attrs?.checkbox) ? `[${item.attrs?.checked ? 'x' : ' '}] ` : '';
  const line = `${'  '.repeat(depth)}- ${box}${inlineToMarkdown(para?.content)}
`;
  return line + children.map((c) => namedListItemToMarkdown(c, depth + 1, kind, false)).join('');
}

function nodeToMarkdown(node: JSONContent, listDepth = 0): string {
  const indent = '  '.repeat(listDepth);

  switch (node.type) {
    case 'paragraph': {
      const pIndent = '    '.repeat(node.attrs?.indent || 0);
      return pIndent + flagPrefix(node) + inlineToMarkdown(node.content) + '\n' + flagReplies(node);
    }

    case 'heading': {
      const level = node.attrs?.level ?? 1;
      const hashes = '#'.repeat(level);
      const hIndent = '    '.repeat(node.attrs?.indent || 0);
      return `${hIndent}${hashes} ${flagPrefix(node)}${inlineToMarkdown(node.content)}\n${flagReplies(node)}`;
    }

    case 'bulletList':
      return (node.content ?? [])
        .map(item => listItemToMarkdown(item, listDepth, '-'))
        .join('\n') + '\n';

    case 'orderedList':
      return (node.content ?? [])
        .map((item, i) => listItemToMarkdown(item, listDepth, `${i + 1}.`))
        .join('\n') + '\n';

    case 'taskList':
      return (node.content ?? [])
        .map(item => {
          const checked = item.attrs?.checked ? 'x' : ' ';
          const text = inlineFromListItem(item);
          return `${indent}- [${checked}] ${text}`;
        })
        .join('\n') + '\n';

    case 'namedList': {
      // A named list (#337): its name, then its items as a Markdown list.
      const kind = node.attrs?.kind === 'plain' ? 'plain' : 'checklist';
      const items = (node.content ?? []).map((item) => namedListItemToMarkdown(item, 0, kind, true)).join('');
      return `**${node.attrs?.name ?? 'List'}**
${items}`;
    }

    case 'listItem': {
      return `${indent}${inlineFromListItem(node)}`;
    }

    case 'codeBlock': {
      const lang = node.attrs?.language ?? '';
      const code = (node.content ?? []).map(c => c.text ?? '').join('');
      return `\`\`\`${lang}\n${code}\n\`\`\`\n`;
    }

    case 'blockquote':
      return (node.content ?? [])
        .map(child => '> ' + nodeToMarkdown(child).trimEnd())
        .join('\n') + '\n';

    case 'table':
      return tableToMarkdown(node) + '\n';

    case 'horizontalRule':
      return '---\n';

    case 'image': {
      const alt = node.attrs?.alt ?? '';
      const src = node.attrs?.src ?? '';
      return `![${alt}](${src})\n`;
    }

    case 'details': {
      const summary = node.attrs?.summary ?? 'Details';
      const inner = (node.content ?? []).map(n => nodeToMarkdown(n)).join('\n');
      return `<details>\n<summary>${summary}</summary>\n\n${inner}\n</details>\n`;
    }

    case 'hardBreak':
      return '\n';

    case 'excerptEmbed':
      return excerptEmbedToMarkdown(node);

    default:
      // Unknown node type — try to extract text
      if (node.content) {
        return node.content.map(n => nodeToMarkdown(n, listDepth)).join('');
      }
      return node.text ?? '';
  }
}

function listItemToMarkdown(item: JSONContent, depth: number, bullet: string): string {
  const indent = '  '.repeat(depth);
  const text = inlineFromListItem(item);
  // Check for nested lists
  const nested = (item.content ?? [])
    .filter(c => c.type === 'bulletList' || c.type === 'orderedList' || c.type === 'taskList')
    .map(c => nodeToMarkdown(c, depth + 1))
    .join('');
  return `${indent}${bullet} ${text}${nested ? '\n' + nested.trimEnd() : ''}`;
}

function inlineFromListItem(item: JSONContent): string {
  const paragraphs = (item.content ?? []).filter(
    c => c.type === 'paragraph' || c.type === 'text'
  );
  return paragraphs.map(p => inlineToMarkdown(p.content ?? (p.text ? [p] : []))).join(' ').trim();
}

function inlineToMarkdown(content?: JSONContent[]): string {
  if (!content) return '';
  return content.map(node => {
    let text = node.text ?? '';

    // Handle special inline node types
    if (node.type === 'mention') {
      return `@${node.attrs?.label ?? node.attrs?.id ?? ''}`;
    }
    if (node.type === 'entityLink') {
      return `[[${node.attrs?.label ?? node.attrs?.name ?? ''}]]`;
    }
    if (node.type === 'hardBreak') {
      return '\n';
    }
    if (node.type === 'image') {
      return `![${node.attrs?.alt ?? ''}](${node.attrs?.src ?? ''})`;
    }

    // Apply marks
    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'bold':
          case 'strong':
            text = `**${text}**`;
            break;
          case 'italic':
          case 'em':
            text = `*${text}*`;
            break;
          case 'strike':
            text = `~~${text}~~`;
            break;
          case 'underline':
            // Markdown has no underline; inline HTML is the usual convention.
            text = `<u>${text}</u>`;
            break;
          case 'code':
            text = `\`${text}\``;
            break;
          case 'link':
            text = `[${text}](${mark.attrs?.href ?? ''})`;
            break;
          case 'highlight':
            text = `==${text}==`;
            break;
        }
      }
    }

    return text;
  }).join('');
}

/** A node's text, as ProseMirror's textContent reads it (text nodes only). */
function plainText(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  return (node.content ?? []).map(plainText).join('');
}

function tableToMarkdown(node: JSONContent): string {
  const rows = node.content ?? [];
  if (rows.length === 0) return '';

  // Table maths (#338): a formula cell exports its computed value.
  const results = evaluateTable(rows.map(row =>
    (row.content ?? []).map(cell => ({
      text: plainText(cell),
      header: cell.type === 'tableHeader',
      colspan: cell.attrs?.colspan,
      rowspan: cell.attrs?.rowspan,
    }))
  ));

  const tableData: string[][] = rows.map((row, r) =>
    (row.content ?? []).map((cell, c) => {
      const result = results[r]?.[c];
      return result ? formatResult(result) : inlineToMarkdown(cell.content?.[0]?.content);
    })
  );

  if (tableData.length === 0) return '';

  const colCount = Math.max(...tableData.map(r => r.length));
  const colWidths = Array.from({ length: colCount }, (_, i) =>
    Math.max(3, ...tableData.map(row => (row[i] ?? '').length))
  );

  const lines: string[] = [];
  for (let r = 0; r < tableData.length; r++) {
    const cells = Array.from({ length: colCount }, (_, c) =>
      (tableData[r][c] ?? '').padEnd(colWidths[c])
    );
    lines.push('| ' + cells.join(' | ') + ' |');
    if (r === 0) {
      lines.push('| ' + colWidths.map(w => '-'.repeat(w)).join(' | ') + ' |');
    }
  }

  return lines.join('\n');
}

/**
 * Download a string as a Markdown file.
 */
export function downloadMarkdown(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.md') ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger the browser print dialog (which offers "Save as PDF").
 */
export function printAsPdf(): void {
  window.print();
}
